import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Move, Replace } from 'lucide-react';
import {
  REELS_SHORTS_TITLE_TEMPLATES,
  captionLayerForPlacement,
  layerForPlacement,
  placementNearestFromLayer,
  sanitizeTitleOverlay,
  sanitizeTitleStyle,
} from '@/lib/reelsShortsTitle';
import { normalizedLayerToPixelRect } from '@/lib/subtitles/subtitleCoordinates';
import {
  dragSubtitleLayer,
  handlePositionStyle,
  resizeSubtitleLayerFromHandle,
  SUBTITLE_RESIZE_HANDLES,
} from '@/lib/subtitles/subtitleTransform';
import { ReelsTitleFrame } from '@/components/reelsShorts/ReelsShortsTitleTemplates';

const ASPECT = 9 / 16;
const HANDLE_HIT =
  'rounded-sm border border-white/90 bg-rose-400 shadow-md hover:bg-rose-300 touch-none pointer-events-auto';

function layerBoxStyle(layer, frameW, frameH) {
  const rect = normalizedLayerToPixelRect(layer, frameW, frameH);
  return {
    position: 'absolute',
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    transform: rect.rotation ? `rotate(${rect.rotation}deg)` : undefined,
  };
}

/**
 * Interactive 9:16 preview for title placement — drag, resize, swap template.
 */
export function ReelsShortsTitlePlacementPreview({
  enabled = true,
  line1 = '',
  line2 = '',
  templateId,
  style,
  layer,
  captionPlacement = 'bottom',
  previewUrl = '',
  onLayerChange,
  onCaptionPlacementChange,
  onTemplateChange,
  onLinesChange,
}) {
  const frameRef = useRef(null);
  const [frameSize, setFrameSize] = useState({ w: 180, h: 320 });
  const [liveTitle, setLiveTitle] = useState(null);
  const [liveCaption, setLiveCaption] = useState(null);
  const liveRef = useRef(null);
  const sessionRef = useRef(null);
  const [activeBand, setActiveBand] = useState('title'); // title | caption
  const [editing, setEditing] = useState(false);

  const displayLayer = liveTitle || layer || layerForPlacement('top');
  const captionLayer = useMemo(
    () => liveCaption || captionLayerForPlacement(captionPlacement),
    [liveCaption, captionPlacement],
  );
  const look = sanitizeTitleStyle(style);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => {
      const w = Math.max(120, el.clientWidth);
      setFrameSize({ w, h: Math.round(w / ASPECT) });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const endSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      window.removeEventListener('mousemove', session.onMove);
      window.removeEventListener('mouseup', session.onEnd);
      window.removeEventListener('touchmove', session.onTouchMove);
      window.removeEventListener('touchend', session.onEnd);
    }
    const final = liveRef.current;
    liveRef.current = null;
    setLiveTitle(null);
    setLiveCaption(null);
    if (final && session?.kind === 'title') {
      onLayerChange?.(final, placementNearestFromLayer(final));
    }
    if (final && session?.kind === 'caption') {
      onCaptionPlacementChange?.(placementNearestFromLayer(final));
    }
  }, [onLayerChange, onCaptionPlacementChange]);

  const startTitleSession = useCallback(
    (mode, handle, event) => {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      setActiveBand('title');
      const frame = frameRef.current?.getBoundingClientRect();
      if (!frame) return;
      const pt = 'touches' in event ? event.touches[0] : event;
      if (!pt) return;
      const startLayer = { ...(layer || layerForPlacement('top')) };
      const startX = pt.clientX;
      const startY = pt.clientY;
      const fw = Math.max(1, frame.width);
      const fh = Math.max(1, frame.height);

      const onMove = (ev) => {
        const p = 'touches' in ev ? ev.touches[0] : ev;
        if (!p) return;
        const dx = (p.clientX - startX) / fw;
        const dy = (p.clientY - startY) / fh;
        let next = startLayer;
        if (mode === 'drag') {
          next = dragSubtitleLayer(startLayer, dx, dy, ASPECT, {
            positionMode: 'free',
            maxHeight: 0.45,
          });
        } else if (mode === 'resize' && handle) {
          next = resizeSubtitleLayerFromHandle(handle, startLayer, dx, dy, ASPECT, {
            positionMode: 'free',
            minWidth: 0.35,
            minHeight: 0.08,
            maxHeight: 0.45,
          });
        }
        liveRef.current = next;
        setLiveTitle(next);
      };
      const onEnd = () => endSession();
      const onTouchMove = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        onMove(ev);
      };
      sessionRef.current = { kind: 'title', onMove, onEnd, onTouchMove };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [enabled, layer, endSession],
  );

  const startCaptionSession = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setActiveBand('caption');
      const frame = frameRef.current?.getBoundingClientRect();
      if (!frame) return;
      const pt = 'touches' in event ? event.touches[0] : event;
      if (!pt) return;
      const startLayer = captionLayerForPlacement(captionPlacement);
      const startY = pt.clientY;
      const fh = Math.max(1, frame.height);

      const onMove = (ev) => {
        const p = 'touches' in ev ? ev.touches[0] : ev;
        if (!p) return;
        const dy = (p.clientY - startY) / fh;
        const next = dragSubtitleLayer(startLayer, 0, dy, ASPECT, {
          positionMode: 'free',
          maxHeight: 0.16,
        });
        liveRef.current = next;
        setLiveCaption(next);
      };
      const onEnd = () => endSession();
      const onTouchMove = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        onMove(ev);
      };
      sessionRef.current = { kind: 'caption', onMove, onEnd, onTouchMove };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [captionPlacement, endSession],
  );

  useEffect(() => () => endSession(), [endSession]);

  const titleBox = layerBoxStyle(displayLayer, frameSize.w, frameSize.h);
  const captionBox = layerBoxStyle(captionLayer, frameSize.w, frameSize.h);

  return (
    <div className="space-y-3" data-testid="reels-title-placement-preview">
      <div className="flex items-start gap-3">
        <div
          ref={frameRef}
          className="relative mx-auto w-[min(100%,200px)] shrink-0 overflow-hidden rounded-2xl border border-white/15 bg-[#1a1f2b] shadow-inner"
          style={{ aspectRatio: '9 / 16' }}
          data-testid="reels-title-placement-frame"
        >
          {previewUrl ? (
            <video
              src={previewUrl}
              muted
              playsInline
              autoPlay
              loop
              className="absolute inset-0 h-full w-full object-cover opacity-80"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-b from-slate-600/80 via-slate-800 to-slate-950" />
          )}
          <div className="pointer-events-none absolute inset-x-2 top-2 z-[1] rounded-md bg-black/45 px-1.5 py-0.5 text-[9px] text-white/80">
            Drag · resize · swap template
          </div>

          {/* Caption band */}
          <div
            role="button"
            tabIndex={0}
            onMouseDown={startCaptionSession}
            onTouchStart={startCaptionSession}
            className={`z-[20] flex cursor-grab items-center justify-center rounded-md border border-dashed px-1 text-center active:cursor-grabbing ${
              activeBand === 'caption'
                ? 'border-sky-300 bg-sky-400/25 text-sky-50'
                : 'border-white/35 bg-black/35 text-white/80'
            }`}
            style={captionBox}
            data-testid="reels-title-caption-band"
            title="Drag subtitles"
          >
            <span className="text-[9px] font-semibold uppercase tracking-wide">Subtitles</span>
          </div>

          {/* Title layer */}
          {enabled ? (
            <div
              className={`z-[30] overflow-hidden rounded-md border ${
                activeBand === 'title'
                  ? 'border-rose-300 ring-1 ring-rose-300/40'
                  : 'border-white/40'
              } bg-black/10`}
              style={titleBox}
              data-testid="reels-title-layer-box"
              onMouseDown={(e) => {
                if (e.target.closest('[data-resize-handle]')) return;
                startTitleSession('drag', null, e);
              }}
              onTouchStart={(e) => {
                if (e.target.closest('[data-resize-handle]')) return;
                startTitleSession('drag', null, e);
              }}
              onDoubleClick={() => setEditing(true)}
            >
              <div className="pointer-events-none h-full w-full scale-[0.92] origin-top">
                <ReelsTitleFrame
                  templateId={templateId}
                  line1={line1 || 'HOOK TITLE'}
                  line2={line2}
                  style={look}
                />
              </div>
              {SUBTITLE_RESIZE_HANDLES.map((handle) => (
                <div
                  key={handle}
                  data-resize-handle={handle}
                  className={HANDLE_HIT}
                  style={handlePositionStyle(handle, -5, 10)}
                  onMouseDown={(e) => startTitleSession('resize', handle, e)}
                  onTouchStart={(e) => startTitleSession('resize', handle, e)}
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-x-4 top-1/3 rounded-lg border border-dashed border-white/25 bg-black/40 px-2 py-3 text-center text-[10px] text-white/60">
              Title overlay is off
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280] flex items-center gap-1">
            <Replace className="w-3 h-3" /> Replace template
          </p>
          <div className="grid grid-cols-2 gap-1.5 max-h-[280px] overflow-y-auto pr-0.5">
            {(REELS_SHORTS_TITLE_TEMPLATES || []).map((row) => {
              const selected = row.id === templateId;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => onTemplateChange?.(row.id)}
                  className={`rounded-lg border px-2 py-1.5 text-left transition ${
                    selected
                      ? 'border-rose-300 bg-rose-500/15 text-white'
                      : 'border-white/10 bg-black/30 text-[#D1D5DB] hover:border-white/25'
                  }`}
                  data-testid={`reels-title-replace-${row.id}`}
                >
                  <p className="text-[11px] font-semibold leading-tight">{row.label}</p>
                  <p className="mt-0.5 text-[9px] text-[#9CA3AF] line-clamp-2">{row.description}</p>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-1">
            {['top', 'center', 'bottom'].map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onLayerChange?.(layerForPlacement(id), id)}
                className="rounded-full border border-white/15 bg-black/40 px-2 py-0.5 text-[10px] capitalize text-[#E5E7EB] hover:border-rose-300/50"
                data-testid={`reels-title-snap-${id}`}
              >
                Snap {id}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#6B7280] flex items-center gap-1">
            <Move className="w-3 h-3" /> Drag the title box or subtitle band on the preview.
          </p>
        </div>
      </div>

      {editing ? (
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-white/10 bg-black/30 p-2">
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Line 1</span>
            <input
              autoFocus
              value={line1}
              maxLength={80}
              onChange={(e) => onLinesChange?.({ line1: e.target.value, line2 })}
              onBlur={() => setEditing(false)}
              className="h-8 w-full rounded-md border border-white/15 bg-black/40 px-2 text-xs text-white"
              data-testid="reels-title-preview-line1"
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Line 2</span>
            <input
              value={line2}
              maxLength={80}
              onChange={(e) => onLinesChange?.({ line1, line2: e.target.value })}
              onBlur={() => setEditing(false)}
              className="h-8 w-full rounded-md border border-white/15 bg-black/40 px-2 text-xs text-white"
              data-testid="reels-title-preview-line2"
            />
          </label>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[11px] text-[#9CA3AF] hover:text-white underline-offset-2 hover:underline"
        >
          Double-click the title (or tap here) to edit text
        </button>
      )}
    </div>
  );
}

export function buildPlacementOverlayState({
  enabled,
  line1,
  line2,
  templateId,
  placement,
  captionPlacement,
  durationSeconds,
  layer,
  style,
  fallbackTitle = '',
}) {
  return sanitizeTitleOverlay(
    {
      enabled,
      line1,
      line2,
      template_id: templateId,
      placement,
      caption_placement: captionPlacement,
      duration_seconds: Number(durationSeconds) || 5,
      layer,
      style,
    },
    fallbackTitle,
  );
}

export default ReelsShortsTitlePlacementPreview;
